import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import SecureImage from "../components/SecureImage";
import { fetchWithAuthRetry } from "../utils/fetchWithAuthRetry";
import Navigation from "../components/Navigation";
import { useAlert } from "../contexts/AlertContext";
import { getAccessToken } from "../utils/auth";

interface ImageData {
  tag_path: string;
  tag_description: string;
  filename: string;
  url: string;
  status: string;
}

interface PaginatedResponse {
  images: ImageData[];
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

interface LocationDetails {
  id: string;
  downloads_remaining: number;
  downloads_limit: number;
  is_final_download: boolean;
  is_unlimited: boolean;
}

const LIMIT = 50;

const LocationImagesPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = searchParams.get("location");
  const [images, setImages] = useState<ImageData[]>([]);
  const [error, setError] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [locationDetails, setLocationDetails] = useState<LocationDetails | null>(null);
  const { confirm } = useAlert();

  const fetchLocationDetails = useCallback(async () => {
    if (!location) return;

    try {
      const res = await fetchWithAuthRetry(
        `https://api.adjusterphoto.com/images/locations`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${getAccessToken() || ""}`,
          },
          credentials: "include",
        },
        navigate,
      );

      if (!res.ok) return;

      const data = await res.json();
      const loc = data.locations?.find((l: any) => l.location_taken === location);
      if (loc) {
        setLocationDetails({
          id: loc.id,
          downloads_remaining: loc.downloads_remaining,
          downloads_limit: loc.downloads_limit,
          is_final_download: loc.is_final_download,
          is_unlimited: loc.is_unlimited,
        });
      }
    } catch (err) {
      console.error("Failed to fetch location details:", err);
    }
  }, [location, navigate]);

  const fetchImages = useCallback(async (currentOffset: number, append: boolean = false) => {
    if (!location) return;

    setLoading(true);
    try {
      const res = await fetchWithAuthRetry(
        `https://api.adjusterphoto.com/images/by-location?location=${encodeURIComponent(location)}&limit=${LIMIT}&offset=${currentOffset}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${getAccessToken() || ""}`,
          },
          credentials: "include",
        },
        navigate,
      );

      if (!res.ok) throw new Error("Failed to fetch images");

      const data: PaginatedResponse = await res.json();
      const sortedImages = [...data.images].sort((a, b) =>
        a.tag_path.localeCompare(b.tag_path),
      );

      if (append) {
        setImages(prev => [...prev, ...sortedImages]);
      } else {
        setImages(sortedImages);
      }
      setTotal(data.total);
      setHasMore(data.has_more);
      setOffset(currentOffset + data.images.length);
    } catch (err) {
      console.error(err);
      setError("Failed to load images for this location.");
    } finally {
      setLoading(false);
    }
  }, [location, navigate]);

  useEffect(() => {
    setImages([]);
    setOffset(0);
    setHasMore(false);
    setTotal(0);
    setLocationDetails(null);
    fetchImages(0, false);
    fetchLocationDetails();
  }, [location, fetchImages, fetchLocationDetails]);

  const loadMore = () => {
    if (!loading && hasMore) {
      fetchImages(offset, true);
    }
  };

  const handleDownload = async (structured: boolean) => {
    if (!location) return;

    const endpoint = structured ? "zip-structured" : "zip-unstructured";
    const downloadType = structured ? "Folders ZIP" : "Flat ZIP";

    // Check if this is a final download
    const isFinalDownload = locationDetails?.is_final_download && !locationDetails?.is_unlimited;

    if (isFinalDownload) {
      const confirmed = await confirm({
        title: 'Final Download Warning',
        message: 'This is your last download for this location. After downloading, the location and all its images will be permanently deleted. Do you want to continue?',
        variant: 'danger',
        confirmLabel: 'Download & Delete',
        cancelLabel: 'Cancel',
      });
      if (!confirmed) return;
    } else {
      const remainingText = locationDetails?.is_unlimited
        ? ''
        : locationDetails
        ? ` You will have ${locationDetails.downloads_remaining - 1} download${locationDetails.downloads_remaining - 1 !== 1 ? 's' : ''} remaining.`
        : '';

      const confirmed = await confirm({
        title: 'Confirm Download',
        message: `Download "${location}" (${total} images) as ${downloadType}?${remainingText}`,
        variant: 'info',
        confirmLabel: 'Download',
        cancelLabel: 'Cancel',
      });
      if (!confirmed) return;
    }

    try {
      let url = `https://api.adjusterphoto.com/images/${endpoint}?location=${encodeURIComponent(location)}`;
      if (isFinalDownload) {
        url += "&confirm_delete=true";
      }

      const res = await fetchWithAuthRetry(
        url,
        {
          headers: {
            Authorization: `Bearer ${getAccessToken() || ""}`,
          },
          credentials: "include",
        },
        navigate,
      );

      if (!res.ok) throw new Error("Download failed");

      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = blobUrl;
      link.setAttribute("download", `${endpoint}-${location}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);

      // Refresh location details to update download count
      fetchLocationDetails();
    } catch (err) {
      console.error("Download error:", err);
    }
  };

  const handleDeleteLocation = async () => {
    if (!location) return;

    const confirmed = await confirm({
      title: 'Delete Location',
      message: `Are you sure you want to delete "${location}" and all ${total} images? This action cannot be undone.`,
      variant: 'danger',
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
    });
    if (!confirmed) return;

    setDeleting(true);
    try {
      const res = await fetchWithAuthRetry(
        `https://api.adjusterphoto.com/images/delete-location?location=${encodeURIComponent(location)}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${getAccessToken() || ""}`,
          },
          credentials: "include",
        },
        navigate
      );

      if (!res.ok) throw new Error("Failed to delete location");

      navigate("/uploads");
    } catch (err) {
      console.error("Delete error:", err);
      setError("Failed to delete location.");
    } finally {
      setDeleting(false);
    }
  };

  if (!location) {
    return (
      <div className="min-h-screen bg-[#0a0b0f] flex items-center justify-center">
        <p className="text-red-400">Location not provided.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0b0f]">
      <Navigation />
      <div className="p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-semibold text-zinc-100">
                Images from: <span className="text-amber-400">{location}</span>
              </h1>
              <div className="flex items-center gap-3 mt-1">
                {total > 0 && (
                  <p className="text-zinc-500 text-sm">
                    Showing {images.length} of {total} images
                  </p>
                )}
                {locationDetails && (
                  <>
                    {total > 0 && <span className="text-zinc-700">•</span>}
                    {locationDetails.is_unlimited ? (
                      <span className="badge badge-success">Unlimited downloads</span>
                    ) : locationDetails.downloads_remaining === 0 ? (
                      <span className="badge badge-danger">No downloads left</span>
                    ) : locationDetails.is_final_download ? (
                      <span className="badge badge-warning">1 download left (final)</span>
                    ) : (
                      <span className="badge badge-info">
                        {locationDetails.downloads_remaining} of {locationDetails.downloads_limit} downloads remaining
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => handleDownload(false)}
                disabled={locationDetails?.downloads_remaining === 0 && !locationDetails?.is_unlimited}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                  locationDetails?.downloads_remaining === 0 && !locationDetails?.is_unlimited
                    ? 'bg-zinc-800 text-zinc-600'
                    : locationDetails?.is_final_download && !locationDetails?.is_unlimited
                    ? 'bg-rose-500/90 hover:bg-rose-500 text-white hover:shadow-lg hover:shadow-rose-500/25'
                    : 'bg-cyan-500/90 hover:bg-cyan-500 text-zinc-900 hover:shadow-lg hover:shadow-cyan-500/25'
                }`}
              >
                Download Flat
              </button>
              <button
                onClick={() => handleDownload(true)}
                disabled={locationDetails?.downloads_remaining === 0 && !locationDetails?.is_unlimited}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                  locationDetails?.downloads_remaining === 0 && !locationDetails?.is_unlimited
                    ? 'bg-zinc-800 text-zinc-600'
                    : locationDetails?.is_final_download && !locationDetails?.is_unlimited
                    ? 'bg-rose-500/90 hover:bg-rose-500 text-white hover:shadow-lg hover:shadow-rose-500/25'
                    : 'btn-primary'
                }`}
              >
                Download Folders
              </button>
              <button
                onClick={handleDeleteLocation}
                disabled={deleting}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-rose-500/90 hover:bg-rose-500 text-white transition-all hover:shadow-lg hover:shadow-rose-500/25 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none"
              >
                {deleting ? "Deleting..." : "Delete Location"}
              </button>
            </div>
          </div>

          {error && <div className="alert-error mb-6">{error}</div>}

          {/* Table */}
          <div className="card-dark rounded-xl overflow-hidden">
            <table className="table-dark">
              <thead>
                <tr>
                  <th>Preview</th>
                  <th>Filename</th>
                  <th>Tag Path</th>
                  <th>Description</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {images.length === 0 && !loading ? (
                  <tr>
                    <td colSpan={5} className="text-center py-8 text-zinc-500">
                      No images found for this location.
                    </td>
                  </tr>
                ) : (
                  images.map((img, i) => (
                    <tr key={img.filename + i}>
                      <td>
                        <SecureImage filename={img.filename} />
                      </td>
                      <td className="text-sm">{img.filename}</td>
                      <td className="text-sm text-zinc-400">{img.tag_path}</td>
                      <td className="text-sm text-zinc-400">{img.tag_description || "None"}</td>
                      <td>
                        <span className={`badge ${
                          img.status === 'Damaged' ? 'badge-danger' :
                          img.status === 'Fixed' ? 'badge-success' :
                          'badge-info'
                        }`}>
                          {img.status}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Load More Button */}
          {hasMore && (
            <div className="mt-6 text-center">
              <button
                onClick={loadMore}
                disabled={loading}
                className="btn-secondary px-6 py-2.5 rounded-lg disabled:opacity-50"
              >
                {loading ? "Loading..." : `Load More (${total - images.length} remaining)`}
              </button>
            </div>
          )}

          {loading && images.length === 0 && (
            <div className="mt-6 text-center text-zinc-500">
              Loading images...
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LocationImagesPage;
